import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { srOnly } from '../../components/organic/motion';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { formatCelsius, formatDelta } from '../../utils/thermal/legend';
import { THERMAL_ACCEPT, THERMAL_MAX_BYTES, thermalFileProblem, uploadThermal } from '../../utils/thermal/upload';
import { thermalPath } from '../../components/workbench/thermal/links';
import { refusalText } from '../../components/thermal/thermalText';
import { box, button, input, label, mono } from '../shared/styles';
import ThermalSharing from './ThermalSharing';
import ThermalSeries from './ThermalSeries';
import ThermalTemplate from './ThermalTemplate';

/*
 * The experiment's Thermal & Metabolic tab (docs/FIELD_AI_VISION_SPEC.md
 * section 43, phase C3; GET /experiments/{id}/thermal).
 *
 *   sharing      the level of its captures, its collaborators, its owner
 *   template     the ROIs every capture is measured with
 *   upload       a radiometric file, byte for byte, linked to the experiment
 *                or one unit; the lid state asked when the units are plates
 *   captures     oldest first, each with its units and its current analysis's
 *                ROI values -- the server's -- and what each was recorded as;
 *                Open shows it in the analysis workbench's thermal mode
 *                (/tools/analysis?mode=thermal&capture=&experiment=, 2026-10-26;
 *                the Thermal Analysis Panel over this page before), which takes
 *                the template, and for a capture not yet analysed the ROIs where
 *                they were on the unit's previous capture; its Back comes to
 *                this tab of this experiment again (?experiment=&tab=thermal)
 *   series       one ROI label over time (ThermalSeries.js)
 *   record       chosen ROIs' means, or differences from the reference, as
 *                values of a temperature outcome while the experiment runs
 *                (POST /experiments/{id}/observations/from-thermal)
 *
 * Only captures the reader may see are listed; the others are counted.
 * "Surface temperature" throughout: a proxy for metabolic heat, never a
 * metabolic rate.
 */
const mb = (bytes) => (bytes / (1024 * 1024)).toFixed(0);
const when = (c) => (c.captured_at || c.created_at || '').slice(0, 16).replace('T', ' ');

export default function ThermalTab({ experimentId, onChanged }) {
  const { t } = useTranslation(['experiments', 'thermal']);
  const { t: tThermal } = useTranslation('thermal');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [target, setTarget] = useState('experiment');
  const [lid, setLid] = useState('');
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [picked, setPicked] = useState([]);           // ROI ids to record
  const [outcome, setOutcome] = useState('');
  const [measure, setMeasure] = useState('t_avg');
  const [timepoint, setTimepoint] = useState('');
  const [unitFor, setUnitFor] = useState({});
  const [recordMessage, setRecordMessage] = useState(null);
  const file = useRef(null);

  const load = useCallback(() => api.get(`/experiments/${experimentId}/thermal`)
    .then(({ data: d }) => { setData(d); setError(''); })
    .catch((e) => setError(errorText(e))), [experimentId]);
  useEffect(() => { load(); }, [load]);

  const unitsById = useMemo(() => new Map((data?.units || []).map((u) => [u.unit_id, u])), [data]);
  const rois = useMemo(() => {
    const out = new Map();
    for (const c of data?.captures || []) for (const r of c.analysis?.rois || []) out.set(r.roi_id, { roi: r, capture: c });
    return out;
  }, [data]);

  /** A capture in the workbench's thermal mode, with this experiment's template; Back comes to this tab. */
  const open = (captureId) => navigate(thermalPath({
    capture: captureId, experiment: experimentId,
    back: `${pathname}?${new URLSearchParams({ experiment: experimentId, tab: 'thermal' })}`,
  }));

  if (error && !data) return <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>;
  if (!data) return <p>{t('loading')}</p>;

  const plates = data.units.some((u) => u.plate_id);
  const targets = [{ value: 'experiment', kind: 'experiment', id: data.experiment_id, label: t('thermal.upload.whole') },
    ...data.units.map((u) => ({ value: u.unit_id, kind: 'experiment_unit', id: u.unit_id, label: u.label }))];

  const pick = async (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = '';
    if (!chosen) return;
    const why = thermalFileProblem(chosen);
    if (why) {
      setMessage({ kind: 'error', text: t(`thermal:field.problem.${why}`, { name: chosen.name, max: mb(THERMAL_MAX_BYTES) }) });
      return;
    }
    const link = targets.find((x) => x.value === target) || targets[0];
    setImporting(true);
    setMessage(null);
    const result = await uploadThermal(chosen, { links: [{ kind: link.kind, id: link.id }], lidState: lid || null });
    setImporting(false);
    if (!result.ok) {
      setMessage({ kind: 'error', text: result.unreachable ? t('thermal.upload.offline')
        : refusalText(tThermal, { code: result.code }, result.status) });
      return;
    }
    if (result.capture?.visible === false) setMessage({ kind: 'ok', text: t('thermal.upload.filedHidden') });
    else if (result.refusal) setMessage({ kind: 'warn', text: refusalText(tThermal, result.refusal) });
    else setMessage({ kind: 'ok', text: t('thermal.upload.imported') });
    await load();
    onChanged?.();
    if (result.capture?.visible !== false && result.decoded) open(result.capture.capture_id);
  };

  const toggle = (roiId) => setPicked((list) => (list.includes(roiId) ? list.filter((x) => x !== roiId) : [...list, roiId]));
  // An ROI saved as measuring a unit (0102) is recorded for it; another needs its capture's only unit, or a choice.
  const ambiguous = picked.filter((id) => !rois.get(id)?.roi.unit?.unit_id
    && (rois.get(id)?.capture.unit_ids || []).length !== 1);
  const record = async () => {
    setRecordMessage(null);
    const units = Object.fromEntries(ambiguous.filter((id) => unitFor[id]).map((id) => [id, unitFor[id]]));
    try {
      const { data: done } = await api.post(`/experiments/${experimentId}/observations/from-thermal`, {
        roi_ids: picked, outcome, measure, ...(timepoint.trim() ? { timepoint: timepoint.trim() } : {}),
        ...(Object.keys(units).length ? { units } : {}),
      });
      setRecordMessage({ kind: 'ok', text: t('thermal.record.done', { imported: done.imported, already: done.already }) });
      setPicked([]);
      setUnitFor({});
      await load();
      onChanged?.();
    } catch (e) {
      setRecordMessage({ kind: 'error', text: errorText(e) });
    }
  };

  const canSend = picked.length > 0 && outcome && ambiguous.every((id) => unitFor[id]);
  return (
    <div data-testid="thermal-tab">
      <p style={{ fontSize: 13, marginTop: 0 }}>{t('thermal.intro')}</p>
      {data.hidden > 0 && (
        <p style={{ fontSize: 13 }} data-testid="thermal-hidden">{t('thermal.hidden', { count: data.hidden })}</p>
      )}
      <ThermalSharing experimentId={experimentId} level={data.thermal_visibility} owner={data.owner}
        mayManage={data.may_manage} onChanged={load} />
      <ThermalTemplate experimentId={experimentId} template={data.template} mayEdit={data.may_template}
        onSaved={load} />

      {data.may_link && (
        <section style={box} aria-labelledby="thermal-upload-title">
          <h4 id="thermal-upload-title" style={{ marginTop: 0 }}>{t('thermal.upload.title')}</h4>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={label}>{t('thermal.upload.linkTo')}
              <select style={input} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="thermal-upload-target">
                {targets.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>
            </label>
            {plates && (
              <label style={label}>{t('thermal.upload.lid')}
                <select style={input} value={lid} onChange={(e) => setLid(e.target.value)} data-testid="thermal-upload-lid">
                  <option value="">{t('thermal:lid.unknown')}</option>
                  {['off', 'ir_window', 'closed'].map((s) => <option key={s} value={s}>{t(`thermal:lid.${s}`)}</option>)}
                </select>
              </label>
            )}
            <button type="button" style={{ ...button('var(--c-primary)'), marginBottom: 10 }} disabled={importing}
              onClick={() => file.current?.click()} data-testid="thermal-upload">
              {importing ? t('thermal.upload.importing') : t('thermal.upload.button')}</button>
            <input ref={file} type="file" accept={THERMAL_ACCEPT} style={{ display: 'none' }} tabIndex={-1}
              aria-hidden="true" data-testid="thermal-upload-input" onChange={pick} />
          </div>
          {lid === 'closed' && <p style={{ fontSize: 12, margin: 0 }}>{t('thermal:lid.closedWarning')}</p>}
          {message && (
            <p role={message.kind === 'ok' ? 'status' : 'alert'} data-testid="thermal-upload-message" data-kind={message.kind}
              style={{ margin: '6px 0 0', fontSize: 13 }}>{message.text}</p>
          )}
        </section>
      )}

      <section style={box} aria-labelledby="thermal-captures-title">
        <h4 id="thermal-captures-title" style={{ marginTop: 0 }}>{t('thermal.captures.title', { count: data.captures.length })}</h4>
        {data.captures.length === 0 && <p style={{ fontSize: 13, margin: 0 }} data-testid="thermal-captures-empty">{t('thermal.captures.empty')}</p>}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.captures.map((c) => {
            const thumb = resolvePhotoUrl(c.urls?.thumb);
            const unitLabels = c.unit_ids.map((id) => unitsById.get(id)?.label).filter(Boolean);
            const a = c.analysis;
            return (
              <li key={c.capture_id} data-testid={`thermal-capture-${c.capture_id}`}
                style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start',
                  borderBlockEnd: '1px solid var(--c-border)', paddingBlockEnd: 10 }}>
                <button type="button" onClick={() => open(c.capture_id)} data-testid={`thermal-open-${c.capture_id}`}
                  title={t('thermal.captures.openTip')}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', padding: 4,
                    background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 8,
                    cursor: 'pointer', color: 'var(--c-text-fg)', font: 'inherit' }}>
                  {thumb ? <img src={thumb} alt="" width={120} height={90}
                    style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, background: '#000' }} />
                    : <span aria-hidden="true" style={{ width: 120, height: 90, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 28 }}>🌡️</span>}
                  <span style={{ fontSize: 12 }}>{t('thermal.captures.open')}</span>
                </button>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 13 }}>
                    <bdi dir="ltr">{when(c)}</bdi>
                    {' · '}{unitLabels.length ? unitLabels.join(', ') : t('thermal.upload.whole')}
                    {c.lid_state ? ` · ${t(`thermal:lid.${c.lid_state}`)}` : ''}
                    {' · '}<span style={mono}>{c.sha256.slice(0, 12)}</span>
                  </p>
                  {c.status !== 'decoded' && <p style={{ fontSize: 12, margin: 0 }}>{t('thermal.captures.undecoded')}</p>}
                  {c.status === 'decoded' && !a && <p style={{ fontSize: 12, margin: 0 }}>{t('thermal.captures.notAnalysed')}</p>}
                  {a && (
                    <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}
                      data-testid={`thermal-readouts-${c.capture_id}`}>
                      <thead>
                        <tr>
                          {data.may_record && <th><span style={srOnly}>{t('thermal.record.select')}</span></th>}
                          <th style={{ textAlign: 'start' }}>{t('thermal:table.label')}</th>
                          <th>{t('thermal:table.min')}</th>
                          <th>{t('thermal:table.max')}</th>
                          <th>{t('thermal:table.avg')}</th>
                          <th>{a.reference_label ? t('thermal.captures.deltaTo', { label: a.reference_label }) : t('thermal:table.delta')}</th>
                          <th style={{ textAlign: 'start' }}>{t('thermal.captures.recorded')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.rois.map((r) => (r.redacted ? (
                          // A region on a record this reader may not read: its place only (plan phase 10).
                          <tr key={r.roi_id} data-testid={`thermal-redacted-${r.roi_id}`}>
                            {data.may_record && <td />}
                            <td colSpan={6} style={{ color: 'var(--c-text-muted-fg)' }}>{t('thermal.captures.redacted')}</td>
                          </tr>
                        ) : (
                          <tr key={r.roi_id}>
                            {data.may_record && (
                              <td>
                                <input type="checkbox" checked={picked.includes(r.roi_id)} onChange={() => toggle(r.roi_id)}
                                  aria-label={t('thermal.record.pick', { label: r.label })}
                                  data-testid={`thermal-pick-${r.roi_id}`} />
                              </td>
                            )}
                            <td>
                              <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                                background: r.colour, marginInlineEnd: 4 }} />{r.label}
                              {r.unit && <span style={{ color: 'var(--c-text-muted-fg)' }} data-testid={`thermal-roi-unit-${r.roi_id}`}> · {r.unit.label}</span>}
                            </td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{formatCelsius(r.t_min_c, 1)}</bdi></td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{formatCelsius(r.t_max_c, 1)}</bdi></td>
                            <td style={{ textAlign: 'end' }}><bdi dir="ltr">{formatCelsius(r.t_avg_c, 2)}</bdi></td>
                            <td style={{ textAlign: 'end' }}>
                              <bdi dir="ltr">{a.reference_label && r.label !== a.reference_label ? formatDelta(r.delta_avg_c, 2) : '—'}</bdi>
                            </td>
                            <td data-testid={`thermal-recorded-${r.roi_id}`}>{(r.recorded || []).map((x) => x.outcome).join(', ')}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <ThermalSeries captures={data.captures} units={data.units} template={data.template} onOpen={open} />

      {data.may_record && (
        <section style={box} aria-labelledby="thermal-record-title" data-testid="thermal-record">
          <h4 id="thermal-record-title" style={{ marginTop: 0 }}>{t('thermal.record.title')}</h4>
          <p style={{ fontSize: 13, marginTop: 0 }}>{t('thermal.record.intro')}</p>
          {data.outcomes.length === 0 ? (
            <p style={{ fontSize: 13 }} data-testid="thermal-record-none">{t('thermal.record.noOutcome')}</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={label}>{t('outcome')}
                  <select style={input} value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="thermal-record-outcome">
                    <option value="">—</option>
                    {data.outcomes.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                </label>
                <label style={label}>{t('thermal.record.measure')}
                  <select style={input} value={measure} onChange={(e) => setMeasure(e.target.value)} data-testid="thermal-record-measure">
                    <option value="t_avg">{t('thermal.record.measure_t_avg')}</option>
                    <option value="delta_avg">{t('thermal.record.measure_delta_avg')}</option>
                  </select>
                </label>
                <label style={label}>{t('timepoint')}
                  <input style={{ ...input, width: 100 }} value={timepoint} placeholder={data.design_kind === 'repeated' ? 'd7' : ''}
                    onChange={(e) => setTimepoint(e.target.value)} data-testid="thermal-record-timepoint" />
                </label>
              </div>
              {data.design_kind === 'repeated' && <p style={{ fontSize: 12, margin: '0 0 8px' }}>{t('thermal.record.timepointHelp')}</p>}
              {ambiguous.map((id) => (
                <label key={id} style={label}>{t('thermal.record.unitFor', { label: rois.get(id)?.roi.label || '' })}
                  <select style={input} value={unitFor[id] || ''} data-testid={`thermal-record-unit-${id}`}
                    onChange={(e) => setUnitFor((m) => ({ ...m, [id]: e.target.value }))}>
                    <option value="">—</option>
                    {((rois.get(id)?.capture.unit_ids || []).length ? rois.get(id).capture.unit_ids.map((u) => unitsById.get(u))
                      : data.units).filter(Boolean).map((u) => <option key={u.unit_id} value={u.unit_id}>{u.label}</option>)}
                  </select>
                </label>
              ))}
              <button type="button" style={button('var(--c-primary)')} disabled={!canSend} onClick={record}
                data-testid="thermal-record-save">{t('thermal.record.button', { count: picked.length })}</button>
            </>
          )}
          {recordMessage && (
            <p role={recordMessage.kind === 'ok' ? 'status' : 'alert'} data-testid="thermal-record-message"
              style={{ margin: '6px 0 0', fontSize: 13, color: recordMessage.kind === 'ok' ? undefined : 'var(--c-danger-fg)' }}>
              {recordMessage.text}</p>
          )}
        </section>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </div>
  );
}
